/* istanbul ignore file */
import { Injectable } from '@angular/core';
import { ClientData } from '@interfaces';
import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { ISubmitResponse } from '@interfaces/models/ISubmitResponse';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AccountTypes, FilteredAccount, IRAOptions, IRATypes } from '@shared/models';
import { RelationshipDesignationTypes } from '@shared/models/relationship-designation-types';
import {
  submitOpenAccount,
  submitOpenAccountResponse,
  UpdateDisplayedAccounts,
  UpdateHasEligibleExistingAccount,
  UpdateIraOption,
  UpdateIraType,
  UpdateOtherBene,
  UpdateRelationshipDesignation,
  UpdateSelectedAccountsFrom,
  UpdateSelectedAccountTo,
  UpdateTransactionType,
} from '@store/actions';
import { EstateUpdate } from '@store/actions/estate/estate.actions';
import { appianSubmit } from '@store/actions/submit/submit.actions';
import {
  UpdateAccountTypeSelection,
  UpdateSpouseStatusSelection,
  UpdateTriageSelections,
} from '@store/actions/triage/triage.actions';
import * as fromRootStore from '@store/index';
import {
  initialState as selectedClientAccountInitialState,
  TransactionTypesEnum,
} from '@store/reducers/selected-client-accounts/selected-client-accounts.reducer';
import { ISelectedAccount } from '@store/reducers/selected-client-accounts/selected-client-accounts.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { initialState as guideInitialState } from '@store/reducers/triage/triage.reducer';
import {
  selectEligibleClientAccount,
  selectEligibleJointAccounts,
  selectEligibleMinorAccounts,
} from '@store/selectors/client-accounts/client-accounts.selectors';
import { formatSSN } from '@store/selectors/receipt/cashier-nonretirement';
import { switchMap, withLatestFrom } from 'rxjs';
import {
  CitizenshipStatus,
  EmploymentStatus,
  LoadClientPersonalInfoSuccess,
  LoadClientSSNSuccess,
  UpdateAssociationsValidity,
  UpdateEmploymentAssociations,
  UpdateEmploymentStatus,
  UpdateEmploymentStatusValidity,
  UpdateFinancials,
  UpdateFinancialsStatus,
  UpdateInformationIsCorrect,
  UpdateInvestmentObjectives,
  UpdateInvestmentObjectivesStatus,
  UpdatePersonalAddress,
  UpdatePersonalAddressValidity,
  UpdatePhoneNumber,
} from 'vg-interaction-model';
import { IFinancialsState } from 'vg-interaction-model/dist/lib/cdd/store/reducers/financials.reducer';

import {
  testMonkeyComplete,
  testMonkeyContinueScenario,
  testMonkeyMoveExisting,
  testMonkeyMoveNew,
  testMonkeyNextScenario,
  testMonkeyPopulateScenario,
  testMonkeyResetState,
  testMonkeyStop,
  testMonkeyUpdateResponses,
  testMonkeyUpdateScenarios,
  testMonkeyUpdateSelectedAccountTo,
  testMonkeyUpdateSelectedJointAccounts,
  testMonkeyUpdateSelectedMinorAccounts,
  tMonkeyIndvExecNonNew,
  tMonkeyIndvExecSpNew,
  tMonkeyIndvMinorEx,
  tMonkeyIndvMinorNew,
  tMonkeyIndvNonEx,
  tMonkeyIndvNonNew,
  tMonkeyIndvSpEx,
  tMonkeyIndvSpNew,
  tMonkeyIRANonRothNew,
  tMonkeyIRANonRothNewByRelationship,
  tMonkeyIRANonTradNew,
  tMonkeyIRANonTradNewByRelationship,
  tMonkeyIRASpAssumeRothEx,
  tMonkeyIRASpAssumeRothNew,
  tMonkeyIRASpAssumeTradEx,
  tMonkeyIRASpAssumeTradNew,
  tMonkeyIRASpInheritedRothNew,
  tMonkeyIRASpInheritedTradNew,
  tMonkeyJointNonEx,
  tMonkeyJointNonNew,
  tMonkeyJointSpEx,
  tMonkeyJointSpNew,
} from '../actions/test-monkey.actions';
import {
  selectTestMonkeyBranches,
  selectTestMonkeyState,
} from '../selectors/test-monkey.selectors';

@Injectable()
export class TestMonkeyEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
  ) {}

  getRandomNumber(min: number, max: number) {
    return Math.floor(Math.random() * (max - min) + min);
  }

  filters = new Set([
    'selectAll',
    'moveExisting',
    'moveNew',
    'ira',
    'joint',
    'indv',
    'deathVerified',
  ]);

  testMonkeyMap = {
    iraSpAssumeTradNew: tMonkeyIRASpAssumeTradNew(),
    iraSpAssumeRothNew: tMonkeyIRASpAssumeRothNew(),

    iraSpAssumeTradExisting: tMonkeyIRASpAssumeTradEx(),
    iraSpAssumeRothExisting: tMonkeyIRASpAssumeRothEx(),

    iraSpInheritedTradNew: tMonkeyIRASpInheritedTradNew(),
    iraSpInheritedRothNew: tMonkeyIRASpInheritedRothNew(),
    iraNonTradNew: tMonkeyIRANonTradNew(),
    iraNonRothNew: tMonkeyIRANonRothNew(),
    iraNonTradNewByRelationship: tMonkeyIRANonTradNewByRelationship(),
    iraNonRothNewByRelationship: tMonkeyIRANonRothNewByRelationship(),

    jointSpEx: tMonkeyJointSpEx(),
    jointNonEx: tMonkeyJointNonEx(),
    jointSpNew: tMonkeyJointSpNew(),
    jointNonNew: tMonkeyJointNonNew(),

    indvSpEx: tMonkeyIndvSpEx(),
    indvNonEx: tMonkeyIndvNonEx(),
    indvSpNew: tMonkeyIndvSpNew(),
    indvNonNew: tMonkeyIndvNonNew(),
    indvSpExecNew: tMonkeyIndvExecSpNew(),
    indvNonExecNew: tMonkeyIndvExecNonNew(),

    indvMinorEx: tMonkeyIndvMinorEx(),
    indvMinorNew: tMonkeyIndvMinorNew(),
  };

  newAccountStub: ISelectedAccount = {
    accountData: {} as FilteredAccount,
    accountNumber: 'newAccount',
    index: '-1',
  };

  // personalInfo = {
  //   firstName: 'Izzy',
  //   middleName: 'Orangutan',
  //   lastName: 'Taylor',
  //   suffix: SuffixEnum.None,
  //   birthDate: '01/01/1971',
  //   citizenshipStatus: 'US',
  //   countryOfResidence: '',
  //   email: 'test_monkey@vanguard.com',
  //   gender: '',
  // }

  clientDataStub: ClientData = {
    contactInfo: {
      primaryEmailAddress: 'test_monkey@vanguard.com',
    },

    birthDate: '01/01/1971',
    citizenship: 'US_CITIZEN',
    firstName: 'Izzy',
    middleName: 'Orangutan',
    lastName: 'Taylor',
    tin_private: '123-45-6789',
    phoneNumber: '222-444-6688',
    isMobile: false,
    incapacitated: false,
    gender: 'M',
  };

  personalAddress = {
    mailingAddress: {
      street: '123 Test Monkey Ave',
      city: 'Testaloosca',
      state: 'AL',
      zipCode: '90210',
      addressLines: ['123 Test Monkey Ave', '', '', ''],
    },
    streetAddress: {
      street: '123 Test Monkey Ave',
      city: 'Testaloosca',
      state: 'AL',
      zipCode: '90210',
    },
    mailingAddressDropdown: '',
    streetAddressDropdown: '',
    isStreetAddressSame: true,
  };

  testMonkeyTradNew(hasExisting: boolean) {
    return [
      UpdateIraType({ payload: IRATypes.OTHER }),
      UpdateHasEligibleExistingAccount({ payload: hasExisting }),
      UpdateTransactionType({ payload: TransactionTypesEnum.NEW_ACCOUNT }),
    ];
  }

  testMonkeyRothNew(hasExisting: boolean) {
    return [
      UpdateIraType({ payload: IRATypes.ROTH }),
      UpdateHasEligibleExistingAccount({ payload: hasExisting }),
      UpdateTransactionType({ payload: TransactionTypesEnum.NEW_ACCOUNT }),
    ];
  }

  resetState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(testMonkeyResetState),
      switchMap(() => {
        const emptyAddress = {
          street: '',
          city: '',
          state: '',
          zipCode: '',
        };

        return [
          UpdateTriageSelections({
            payload: {
              ...guideInitialState,
              isClient: 'YES',
            },
          }),
          UpdateSelectedAccountTo({
            payload: selectedClientAccountInitialState.transferTo.selectedAccount,
          }),
          UpdateSelectedAccountsFrom({
            payload: selectedClientAccountInitialState.transferFrom.selectedAccounts,
          }),

          //ira
          UpdateIraOption({ payload: IRAOptions.UNKNOWN }),
          UpdateIraType({ payload: IRATypes.UNKNOWN }),
          UpdateHasEligibleExistingAccount({ payload: null }),
          UpdateTransactionType({ payload: TransactionTypesEnum.UNKNOWN }),

          //new account
          LoadClientPersonalInfoSuccess({
            payload: {
              firstName: '',
              middleName: '',
              lastName: '',
              email: '',
              birthDate: '',
              citizenshipStatus: CitizenshipStatus.Empty,
              countryOfResidence: '',
              gender: '',
              suffix: '',
            },
          }),
          LoadClientSSNSuccess({ payload: formatSSN('') }),

          new UpdatePersonalAddress({
            mailingAddress: emptyAddress,
            streetAddress: emptyAddress,
            mailingAddressDropdown: '',
            streetAddressDropdown: '',
          }),
          new UpdatePersonalAddressValidity(''),

          new UpdateEmploymentStatus({
            employmentStatus: EmploymentStatus.UNKNOWN,
            employer: '',
            occupation: '',
            address: emptyAddress,
          }),
          new UpdateEmploymentStatusValidity(''),

          new UpdateEmploymentAssociations({
            spouse: false,
            associated: false,
            control: false,
            none: false,
          }),
          new UpdateAssociationsValidity(''),

          new UpdateInvestmentObjectives({
            objectives: {
              CAP: false,
              Income: false,
              Growth: false,
              Speculation: false,
            },
          }),

          new UpdateFinancials({
            annualIncome: '',
            liquidNetWorth: '',
          } as IFinancialsState),
          new UpdateFinancialsStatus(''),

          submitOpenAccountResponse({
            payload: {} as ISubmitResponse,
          }),
        ];
      }),
    ),
  );

  testMonkeyContinueScenario$ = createEffect(() =>
    this.actions$.pipe(
      ofType(testMonkeyContinueScenario),
      switchMap(() => {
        return [testMonkeyNextScenario()];
      }),
    ),
  );

  testMonkeyNextScenario$ = createEffect(() =>
    this.actions$.pipe(
      ofType(testMonkeyNextScenario),
      withLatestFrom(this.store$.pipe(select(selectTestMonkeyBranches))),
      switchMap(([, branches]) => {
        for (const branch of Object.entries(branches)) {
          const flowKey = branch[0];
          const isEnabled = branch[1];
          //find the next true value excluding the filter flags
          if (!this.filters.has(flowKey) && isEnabled) {
            //set the flag to false
            const disableFlag = testMonkeyUpdateScenarios({ payload: { [flowKey]: false } });
            //dispatch the corresponding flow action using the form key
            const flowAction = this.testMonkeyMap[flowKey];
            return [disableFlag, flowAction];
          }
        }
        return [testMonkeyComplete()];
      }),
    ),
  );

  testMonkeyPopulateScenario$ = createEffect(() =>
    this.actions$.pipe(
      ofType(testMonkeyPopulateScenario),
      withLatestFrom(this.store$.pipe(select(selectTestMonkeyState))),
      switchMap(([, state]) => {
        const actions = [];

        if (state.isRunning) {
          if (state.isNew) {
            actions.push(submitOpenAccount());
          } else {
            actions.push(appianSubmit());
          }
        } else {
          actions.push(
            testMonkeyStop(),
            testMonkeyUpdateResponses({ payload: ['N/A', 'State Set'] }),
          );
        }
        return actions;
      }),
    ),
  );

  testMonkeyJointFromAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(testMonkeyUpdateSelectedJointAccounts),
      withLatestFrom(this.store$.pipe(select(selectEligibleJointAccounts()))),
      switchMap(([, eligibleClientAccount]) => {
        //Create an array of numbers
        const marbles = [];
        for (let i = 0; i < eligibleClientAccount.accounts.length; i++) {
          marbles.push(i);
        }

        //Randomly choose a number of accounts
        const selections = this.getRandomNumber(0, eligibleClientAccount.accounts.length);
        const bag = new Set();

        //Chose a marble to add to the bag
        for (let i = 0; i <= selections; i++) {
          const index = this.getRandomNumber(0, marbles.length);
          bag.add(marbles[index]);
          marbles.splice(index, 1);
        }

        const lotteryAccounts = [];
        eligibleClientAccount.accounts.forEach((account: FilteredAccount, i: number) => {
          if (bag.has(i)) {
            const temp = {
              index: i.toString(),
              accountNumber: account.accountNumber,
              accountData: account,
            };
            lotteryAccounts.push(temp);
          }
        });

        return [
          UpdateDisplayedAccounts({ payload: eligibleClientAccount.accounts }),
          UpdateSelectedAccountsFrom({ payload: lotteryAccounts }),
        ];
      }),
    ),
  );

  testMonkeyMinorFromAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(testMonkeyUpdateSelectedMinorAccounts),
      withLatestFrom(this.store$.pipe(select(selectEligibleMinorAccounts))),
      switchMap(([, eligibleClientAccount]) => {
        //Create an array of numbers
        const marbles = [];
        for (let i = 0; i < eligibleClientAccount.accounts.length; i++) {
          marbles.push(i);
        }

        //Randomly choose a number of accounts
        const selections = this.getRandomNumber(0, eligibleClientAccount.accounts.length);
        const bag = new Set();

        //Chose a marble to add to the bag
        for (let i = 0; i <= selections; i++) {
          const index = this.getRandomNumber(0, marbles.length);
          bag.add(marbles[index]);
          marbles.splice(index, 1);
        }

        const lotteryAccounts = [];
        eligibleClientAccount.accounts.forEach((account, i) => {
          if (bag.has(i)) {
            const temp = {
              index: i.toString(),
              accountNumber: account.accountNumber,
              accountData: account,
            };
            lotteryAccounts.push(temp);
          }
        });

        return [
          UpdateDisplayedAccounts({ payload: eligibleClientAccount.accounts }),
          UpdateSelectedAccountsFrom({ payload: lotteryAccounts }),
        ];
      }),
    ),
  );

  testMonkeyEligibleAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(testMonkeyUpdateSelectedAccountTo),
      withLatestFrom(this.store$.pipe(select(selectEligibleClientAccount()))),
      switchMap(([, eligibleClientAccount]) => {
        const sortedEligibleAccounts = [];

        eligibleClientAccount.accounts.forEach((account) => {
          if (account.productType.includes('BROKERAGE')) {
            // Brokerage accounts added to the top of the list so that they're displayed first.
            sortedEligibleAccounts.unshift(account);
          } else {
            // Other accounts go to the bottom.
            sortedEligibleAccounts.push(account);
          }
        });

        const index = this.getRandomNumber(0, sortedEligibleAccounts.length);
        const account = sortedEligibleAccounts[index];
        const lotteryAccount: ISelectedAccount = {
          accountNumber:
            account.productType === 'Brokerage'
              ? account.brokerageAccountNumber
              : account.positions[0].vastAccountNumber,
          accountData: eligibleClientAccount.accounts[index],
          index: index.toString(),
        };

        return [
          UpdateSelectedAccountTo({ payload: lotteryAccount }),
          UpdateTransactionType({ payload: TransactionTypesEnum.EXISTING }),
        ];
      }),
    ),
  );

  testMonkeyMoveNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(testMonkeyMoveNew),
      withLatestFrom(this.store$.pipe(select(selectTestMonkeyState))),
      switchMap(([, monkeyState]) => {
        const employment = [
          { employmentStatus: EmploymentStatus.RETIRED },
          { employmentStatus: EmploymentStatus.UNEMPLOYED },
          {
            employmentStatus: EmploymentStatus.VANGUARD_EMPLOYED,
            occupation: 'QA Tester',
          },
          {
            employmentStatus: EmploymentStatus.SELF_EMPLOYED,
            employer: 'Test Monkey Inc.',
            occupation: 'QA Tester',
            address: {
              street: '321 Test Monkey Drive',
              city: 'Testaloosca',
              state: 'AL',
              zipCode: '90210',
            },
          },
          {
            employmentStatus: EmploymentStatus.EMPLOYED,
            employer: 'Test Monkey Inc.',
            occupation: 'QA Tester',
            address: {
              street: '321 Test Monkey Drive',
              city: 'Testaloosca',
              state: 'AL',
              zipCode: '90210',
            },
          },
        ];
        const employer = this.getRandomNumber(0, employment.length);

        const financials = 'GHIJKLM';
        const income = financials[this.getRandomNumber(0, financials.length)];
        const netWorth = financials[this.getRandomNumber(0, financials.length)];

        const objectives = {
          CAP: null,
          Income: null,
          Growth: null,
          Speculation: null,
        };

        const values: (keyof typeof objectives)[] = ['CAP', 'Income', 'Growth', 'Speculation'];
        const index = this.getRandomNumber(0, values.length);
        objectives[values[index]] = true;
        if (this.getRandomNumber(1, 2) === 1) {
          const index = this.getRandomNumber(0, values.length);
          objectives[values[index]] = true;
        }

        return [
          LoadClientPersonalInfoSuccess({
            payload: {
              firstName: this.clientDataStub.firstName,
              middleName: this.clientDataStub.middleName,
              lastName: this.clientDataStub.lastName,
              email: this.clientDataStub.contactInfo.primaryEmailAddress,
              birthDate: this.clientDataStub.birthDate,
              citizenshipStatus: this.clientDataStub.citizenship,
              countryOfResidence: '',
              gender: '',
              suffix: '',
            },
          }),
          LoadClientSSNSuccess({ payload: formatSSN(this.clientDataStub.tin_private) }),
          UpdateInformationIsCorrect({ payload: 'Yes' }),
          UpdatePhoneNumber({ payload: this.clientDataStub.phoneNumber }),
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          UpdateTransactionType({ payload: TransactionTypesEnum.NEW_ACCOUNT }),

          new UpdatePersonalAddress(this.personalAddress),
          new UpdatePersonalAddressValidity('VALID'),

          new UpdateEmploymentStatus({
            employmentStatus: employment[employer].employmentStatus,
          }),
          new UpdateEmploymentStatus(employment[employer]),
          new UpdateEmploymentStatusValidity('VALID'),

          //Need to consider this closer, employed by VG is associated as is spouse
          new UpdateEmploymentAssociations({
            spouse: false,
            associated: false,
            control: false,
            none: true,
          }),
          new UpdateAssociationsValidity('VALID'),

          new UpdateInvestmentObjectives({
            objectives,
          }),
          new UpdateInvestmentObjectivesStatus('VALID'),

          new UpdateFinancials({
            annualIncome: income,
            liquidNetWorth: netWorth,
          } as IFinancialsState),
          new UpdateFinancialsStatus('VALID'),
        ];
      }),
    ),
  );

  //IRA Flows

  iraSpouseAssumeTraditionalExisting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRASpAssumeTradEx),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.IRA }),
          UpdateIraOption({ payload: IRAOptions.ASSUME }),
          UpdateIraType({ payload: IRATypes.OTHER }),
          UpdateHasEligibleExistingAccount({ payload: true }),
          UpdateTransactionType({ payload: TransactionTypesEnum.EXISTING }),
          testMonkeyUpdateSelectedAccountTo(),
          testMonkeyMoveExisting(),
          testMonkeyPopulateScenario({ payload: 'IRA Spouse Assume Traditional Existing' }),
        ];
      }),
    ),
  );

  iraSpouseAssumeRothExisting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRASpAssumeRothEx),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.IRA }),
          UpdateIraOption({ payload: IRAOptions.ASSUME }),
          UpdateIraType({ payload: IRATypes.ROTH }),
          UpdateHasEligibleExistingAccount({ payload: true }),
          UpdateTransactionType({ payload: TransactionTypesEnum.EXISTING }),
          testMonkeyUpdateSelectedAccountTo(),
          testMonkeyMoveExisting(),
          testMonkeyPopulateScenario({ payload: 'IRA Spouse Assume Roth Existing' }),
        ];
      }),
    ),
  );

  iraSpouseAssumeTraditionalNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRASpAssumeTradNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.IRA }),
          UpdateIraOption({ payload: IRAOptions.ASSUME }),
          ...this.testMonkeyTradNew(true),
          //Choose New
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'IRA Spouse Assume Traditional New' }),
        ];
      }),
    ),
  );

  iraSpouseAssumeRothNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRASpAssumeRothNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.IRA }),
          UpdateIraOption({ payload: IRAOptions.ASSUME }),
          ...this.testMonkeyRothNew(true),
          //Choose New
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'IRA Spouse Assume Roth New' }),
        ];
      }),
    ),
  );

  iraSpouseInheritedTraditionalNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRASpInheritedTradNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.IRA }),
          UpdateIraOption({ payload: IRAOptions.INHERIT }),
          ...this.testMonkeyTradNew(false),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'IRA Spouse Inherited Traditional New' }),
        ];
      }),
    ),
  );

  iraSpouseInheritedRothNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRASpInheritedRothNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.IRA }),
          UpdateIraOption({ payload: IRAOptions.INHERIT }),
          ...this.testMonkeyRothNew(false),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'IRA Spouse Inherited Roth New' }),
        ];
      }),
    ),
  );

  iraNonspouseTraditionalNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRANonTradNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'NO' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.IRA }),
          ...this.testMonkeyTradNew(false),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'IRA Nonspouse Traditional New (Named)' }),
        ];
      }),
    ),
  );

  iraNonspouseRothNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRANonRothNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'NO' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.IRA }),
          ...this.testMonkeyRothNew(false),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'IRA Nonspouse Roth New (Named)' }),
        ];
      }),
    ),
  );

  iraNonspouseTraditionalNewByRelationship$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRANonTradNewByRelationship),
      switchMap(() => {
        const otherBene = [
          {
            name: 'Donkey Kong',
            dateGroup: {
              dob: '01/01/1945',
              dod: '02/02/2015',
            },
            hasChildren: true,
            children: [
              {
                name: 'Diddy Kong',
                dateGroup: {
                  dob: '10/10/1975',
                  dod: '',
                },
              },
            ],
          },
          {
            name: 'Mojo Jojo',
            dateGroup: {
              dob: '01/01/1968',
              dod: '',
            },
            hasChildren: null,
            children: [],
          },
        ];

        return [
          UpdateTriageSelections({
            payload: {
              isClient: 'YES',
              isSpouse: 'NO',
              accountTypeInheriting: 'IRA',
              IRADesignation: 'RELATIONSHIP',
            } as TriageState,
          }),
          ...this.testMonkeyTradNew(false),
          testMonkeyMoveNew(),
          UpdateRelationshipDesignation({ payload: RelationshipDesignationTypes.DESCENDANT }),
          UpdateOtherBene({ payload: otherBene }),
          testMonkeyPopulateScenario({ payload: 'IRA Nonspouse Traditional New (Relationship)' }),
        ];
      }),
    ),
  );

  iraNonspouseRothNewByRelationship$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIRANonRothNewByRelationship),
      switchMap(() => {
        const otherBene = [
          {
            name: 'Donkey Kong',
            dateGroup: {
              dob: '01/01/1945',
              dod: '02/02/2015',
            },
            hasChildren: true,
            children: [
              {
                name: 'Diddy Kong',
                dateGroup: {
                  dob: '10/10/1975',
                  dod: '',
                },
              },
            ],
          },
          {
            name: 'Mojo Jojo',
            dateGroup: {
              dob: '01/01/1968',
              dod: '',
            },
            hasChildren: null,
            children: [],
          },
        ];

        return [
          UpdateTriageSelections({
            payload: {
              isClient: 'YES',
              isSpouse: 'NO',
              accountTypeInheriting: 'IRA',
              IRADesignation: 'RELATIONSHIP',
            } as TriageState,
          }),

          ...this.testMonkeyRothNew(false),
          testMonkeyMoveNew(),
          UpdateRelationshipDesignation({ payload: RelationshipDesignationTypes.DESCENDANT }),
          UpdateOtherBene({ payload: otherBene }),
          testMonkeyPopulateScenario({ payload: 'IRA Nonspouse Roth New (Relationship)' }),
        ];
      }),
    ),
  );
  //Joint Flows

  jointSpouseExisting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyJointSpEx),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.JOINT }),
          testMonkeyUpdateSelectedJointAccounts(),
          testMonkeyUpdateSelectedAccountTo(),
          testMonkeyMoveExisting(),
          testMonkeyPopulateScenario({ payload: 'Joint Spouse Existing' }),
        ];
      }),
    ),
  );

  jointNonspouseExisting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyJointNonEx),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'NO' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.JOINT }),
          testMonkeyUpdateSelectedJointAccounts(),
          testMonkeyUpdateSelectedAccountTo(),
          testMonkeyMoveExisting(),
          testMonkeyPopulateScenario({ payload: 'Joint Nonspouse Existing' }),
        ];
      }),
    ),
  );

  jointSpouseNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyJointSpNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.JOINT }),
          testMonkeyUpdateSelectedJointAccounts(),
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'Joint Spouse New' }),
        ];
      }),
    ),
  );

  jointNonspouseNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyJointNonNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'NO' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.JOINT }),
          testMonkeyUpdateSelectedJointAccounts(),
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'Joint Nonspouse New' }),
        ];
      }),
    ),
  );

  //Individual Flows

  indvSpouseExisting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIndvSpEx),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.INDV }),
          testMonkeyUpdateSelectedAccountTo(),
          testMonkeyMoveExisting(),
          testMonkeyPopulateScenario({ payload: 'Individual Spouse Existing' }),
        ];
      }),
    ),
  );

  indvNonspouseExisting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIndvNonEx),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'NO' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.INDV }),
          testMonkeyUpdateSelectedAccountTo(),
          testMonkeyMoveExisting(),
          testMonkeyPopulateScenario({ payload: 'Individual Nonspouse Existing' }),
        ];
      }),
    ),
  );

  indvSpouseNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIndvSpNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'YES' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.INDV }),
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'Individual Spouse New' }),
        ];
      }),
    ),
  );

  indvNonspouseNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIndvNonNew),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'NO' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.INDV }),
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'Individual Nonspouse New' }),
        ];
      }),
    ),
  );

  indvExecutorNonspouseNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIndvExecNonNew),
      switchMap(() => {
        return [
          UpdateTriageSelections({
            payload: {
              isClient: 'YES',
              isSpouse: 'NO',
              accountTypeInheriting: 'INDV',
              individualDesignation: IndividualDesignationListedTypes.EXECUTOR,
            } as TriageState,
          }),
          EstateUpdate({ payload: { tin_private: '12-3456789' } }),
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          testMonkeyMoveNew(), // Sets up the new account info
          testMonkeyMoveExisting(), // Disables the OA call
          testMonkeyPopulateScenario({ payload: 'Individual Executor Nonspouse New' }),
        ];
      }),
    ),
  );

  indvExecutorSpouseNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIndvExecSpNew),
      switchMap(() => {
        return [
          UpdateTriageSelections({
            payload: {
              isClient: 'YES',
              isSpouse: 'YES',
              accountTypeInheriting: 'INDV',
              individualDesignation: IndividualDesignationListedTypes.EXECUTOR,
            } as TriageState,
          }),
          EstateUpdate({ payload: { tin_private: '12-3456789' } }),
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          testMonkeyMoveNew(), // Sets up the new account info
          testMonkeyMoveExisting(), // Disables the OA call
          testMonkeyPopulateScenario({ payload: 'Individual Executor Spouse New' }),
        ];
      }),
    ),
  );

  // minor helper functions

  indvMinorNew$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIndvMinorNew),
      switchMap(() => {
        return [
          UpdateAccountTypeSelection({ payload: AccountTypes.UGMA_UTMA }),
          testMonkeyUpdateSelectedMinorAccounts(),
          UpdateSelectedAccountTo({ payload: this.newAccountStub }),
          testMonkeyMoveNew(),
          testMonkeyPopulateScenario({ payload: 'Individual Minor New' }),
        ];
      }),
    ),
  );

  indvMinorExisting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(tMonkeyIndvMinorEx),
      switchMap(() => {
        return [
          UpdateSpouseStatusSelection({ payload: 'NO' }),
          UpdateAccountTypeSelection({ payload: AccountTypes.INDV }),
          testMonkeyUpdateSelectedMinorAccounts(),
          testMonkeyUpdateSelectedAccountTo(),
          testMonkeyMoveExisting(),
          testMonkeyPopulateScenario({ payload: 'Individual Nonspouse Existing' }),
        ];
      }),
    ),
  );
}
